import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, QrCode, Package, Barcode, Download, Eye, CheckCircle, AlertTriangle, ShoppingCart, Printer, Boxes, Activity, MapPin, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QRCodeManagement({ user }) {
  const [qrcodes, setQrcodes] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedQR, setSelectedQR] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [cartonsOpen, setCartonsOpen] = useState(false);
  const [cartons, setCartons] = useState([]);
  const [cartonsBatch, setCartonsBatch] = useState(null);
  const [cartonsLoading, setCartonsLoading] = useState(false);
  const [scanActivity, setScanActivity] = useState([]);
  const [productionBatches, setProductionBatches] = useState([]);
  
  const [formData, setFormData] = useState({
    product_id: '',
    product_name: '',
    batch_number: '',
    quantity: 0,
    unit_price: 0,
    production_date: new Date().toISOString().split('T')[0],
    expiry_date: ''
  });

  useEffect(() => {
    fetchQRCodes();
    fetchProducts();
    fetchStats();
    fetchScanActivity();
    fetchProductionBatches();
  }, []);

  // Batches are opened when production asks for materials, so coding a batch is
  // a matter of choosing one — not retyping a number somebody already chose.
  const fetchProductionBatches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/production-batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductionBatches(response.data || []);
    } catch (error) {
      console.error('Failed to fetch production batches');
    }
  };

  const fetchScanActivity = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/carton-scan-activity?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScanActivity(response.data || []);
    } catch (error) {
      console.error('Failed to fetch scan activity');
    }
  };

  const fetchQRCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/product-qrcodes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrcodes(response.data);
    } catch (error) {
      toast.error('Failed to fetch QR codes');
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products');
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/product-qrcodes/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/product-qrcodes`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('QR Code generated successfully!');
      setOpen(false);
      resetForm();
      fetchQRCodes();
      fetchStats();
      fetchProductionBatches();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: '',
      product_name: '',
      batch_number: '',
      quantity: 0,
      unit_price: 0,
      production_date: new Date().toISOString().split('T')[0],
      expiry_date: ''
    });
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setFormData({
        ...formData,
        product_id: product.id,
        product_name: product.name,
        unit_price: product.retail_price || 0
      });
    }
  };

  // Company batch coding (QP-TR-01): CK-DDMMYY from the production date,
  // e.g. CK-140726 = chalk produced 14 July 2026.
  const generateBatchNumber = () => {
    const d = formData.production_date ? new Date(formData.production_date) : new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    setFormData({ ...formData, batch_number: `CK-${dd}${mm}${yy}` });
  };

  // Choosing a batch carries its production date across with it.
  const selectProductionBatch = (batchNumber) => {
    const batch = productionBatches.find((b) => b.batch_number === batchNumber);
    setFormData({
      ...formData,
      batch_number: batchNumber,
      production_date: batch?.production_date || formData.production_date,
    });
  };

  const viewQRCode = (qr) => {
    setSelectedQR(qr);
    setViewOpen(true);
  };

  const downloadQRCode = async (qr) => {
    try {
      const response = await axios.get(`${API}/product-qrcodes/${qr.id}/image`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `QR_${qr.batch_number}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('QR Code downloaded');
    } catch (error) {
      toast.error('Failed to download QR code');
    }
  };

  const viewCartons = async (qr) => {
    setCartonsBatch(qr);
    setCartons([]);
    setCartonsOpen(true);
    setCartonsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/product-qrcodes/${qr.id}/cartons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCartons(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch cartons');
    } finally {
      setCartonsLoading(false);
    }
  };

  const printAllCartons = () => {
    if (!cartons.length || !cartonsBatch) return;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) {
      toast.error('Please allow popups to print labels');
      return;
    }
    const html = `
      <!doctype html>
      <html>
        <head>
          <title>Carton QR Labels - ${cartonsBatch.batch_number}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 16px; color: #111; }
            h1 { font-size: 16px; margin: 0 0 12px; }
            .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
            .label { border: 1px solid #999; padding: 10px; text-align: center; page-break-inside: avoid; }
            .label img { width: 140px; height: 140px; }
            .label .product { font-weight: bold; font-size: 12px; margin-top: 6px; }
            .label .cid { font-family: monospace; font-size: 11px; margin-top: 2px; }
            .label .batch { font-size: 10px; color: #555; }
            .label .brand { font-size: 9px; color: #8FC043; font-weight: bold; margin-top: 4px; }
            @media print { body { padding: 8px; } .no-print { display:none; } }
          </style>
        </head>
        <body>
          <h1>Himan Investment &amp; Company - Carton QR Labels<br/>
            <small>Batch: ${cartonsBatch.batch_number} | ${cartons.length} cartons</small>
          </h1>
          <div class="grid">
            ${cartons.map(c => `
              <div class="label">
                <img src="${API}/cartons/${c.id}/qr-image" alt="QR" />
                <div class="product">${c.product_name}</div>
                <div class="cid">${c.id}</div>
                <div class="batch">Batch: ${c.batch_number}</div>
                <div class="brand">HIMAN INVESTMENT</div>
              </div>
            `).join('')}
          </div>
          <div class="no-print" style="margin-top:16px;text-align:center;">
            <button onclick="window.print()" style="padding:8px 16px;background:#8FC043;color:#fff;border:0;border-radius:4px;cursor:pointer;">Print Labels</button>
          </div>
        </body>
      </html>`;
    win.document.write(html);
    win.document.close();
  };

  const downloadCartonQR = async (carton) => {
    try {
      const response = await axios.get(`${API}/cartons/${carton.id}/qr-image`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Carton_${carton.id}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download carton QR');
    }
  };

  const filteredQRCodes = activeTab === 'all' 
    ? qrcodes 
    : qrcodes.filter(qr => qr.status === activeTab);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">QR Code & Barcode Management</h1>
          <p className="text-gray-600">Generate and track QR codes for finished products</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#8FC043] hover:bg-[#7aa936]" onClick={resetForm}>
              <Plus size={16} className="mr-2" />
              Generate QR Code
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Generate Product QR Code</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Select Product *</Label>
                <Select onValueChange={handleProductSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} - {product.category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="batch_number">Batch Number *</Label>
                {productionBatches.length > 0 && (
                  <Select
                    value={productionBatches.some((b) => b.batch_number === formData.batch_number) ? formData.batch_number : ''}
                    onValueChange={selectProductionBatch}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a production batch" />
                    </SelectTrigger>
                    <SelectContent>
                      {productionBatches.map((batch) => (
                        <SelectItem key={batch.batch_number} value={batch.batch_number}>
                          {batch.batch_number} — {batch.production_date}
                          {batch.has_qr ? ' · already coded' : ''}
                          {batch.purposes.length ? ` · ${batch.purposes[0]}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex gap-2">
                  <Input
                    id="batch_number"
                    value={formData.batch_number}
                    onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })}
                    required
                    placeholder="CK-DDMMYY"
                  />
                  <Button type="button" variant="outline" onClick={generateBatchNumber}>
                    Auto
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Pick the batch production opened, or let Auto write CK-DDMMYY from the production date.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity (Cartons) *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                    required
                    min="1"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit_price">Unit Price (Tsh)</Label>
                  <Input
                    id="unit_price"
                    type="number"
                    value={formData.unit_price}
                    onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="production_date">Production Date *</Label>
                  <Input
                    id="production_date"
                    type="date"
                    value={formData.production_date}
                    onChange={(e) => setFormData({ ...formData, production_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiry_date">Expiry Date</Label>
                  <Input
                    id="expiry_date"
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-800">
                  <QrCode size={14} className="inline mr-1" />
                  A unique QR code will be generated for <b>each individual carton/box</b> in this batch.
                  Each carton gets a unique ID like <code>YYYYMMDD-Cnnnn</code> for precise tracking.
                </p>
              </div>
              
              <Button type="submit" className="w-full bg-[#8FC043] hover:bg-[#7aa936]">
                <QrCode size={16} className="mr-2" />
                Generate QR Code & Barcode
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <QrCode className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total QR Codes</p>
                <p className="text-2xl font-bold">{stats.total_qrcodes || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Batches</p>
                <p className="text-2xl font-bold text-green-600">{stats.active_qrcodes || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Package className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Produced</p>
                <p className="text-2xl font-bold text-purple-600">{stats.total_produced || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-orange-100 p-3 rounded-lg">
                <ShoppingCart className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Sold</p>
                <p className="text-2xl font-bold text-orange-600">{stats.total_sold || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scan Activity Feed */}
      <Card data-testid="scan-activity-feed">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity size={18} className="text-[#EA5829]" />
            Recent Customer Scans
            <Badge variant="outline" className="ml-2 font-normal">
              {scanActivity.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {scanActivity.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No customer scans yet. Scans will appear here when customers verify their cartons.
            </p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {scanActivity.map((s, idx) => (
                <div
                  key={`${s.carton_id}-${s.scanned_at}-${idx}`}
                  className="flex items-start justify-between gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  data-testid={`scan-activity-item-${idx}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-gray-900">{s.customer_name}</span>
                      <Badge
                        className={
                          s.status === 'sold-via-scan'
                            ? 'bg-orange-100 text-orange-800'
                            : s.status === 'delivered'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }
                      >
                        {s.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      <span className="font-mono">{s.carton_id}</span>
                      <span>•</span>
                      <span>{s.product_name}</span>
                      {s.location && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <MapPin size={11} />
                            {s.location}
                          </span>
                        </>
                      )}
                      {s.customer_email && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Mail size={11} />
                            {s.customer_email}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {new Date(s.scanned_at).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All QR Codes</TabsTrigger>
          <TabsTrigger value="active" className="text-green-600">Active</TabsTrigger>
          <TabsTrigger value="depleted" className="text-gray-600">Depleted</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <div className="bg-white rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>QR Code</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Batch #</TableHead>
                  <TableHead>Barcode</TableHead>
                  <TableHead>Initial Qty</TableHead>
                  <TableHead>Remaining</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Production Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQRCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-slate-500 py-12">
                      <QrCode size={48} className="mx-auto text-gray-400 mb-4" />
                      <p className="text-lg font-semibold mb-2">No QR codes generated yet</p>
                      <p className="text-sm">Generate your first QR code for product tracking</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredQRCodes.map((qr) => (
                    <TableRow key={qr.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <img 
                            src={`${API}/product-qrcodes/${qr.id}/image`} 
                            alt="QR Code" 
                            className="w-12 h-12 border rounded"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{qr.product_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{qr.batch_number}</Badge>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">{qr.barcode}</code>
                      </TableCell>
                      <TableCell>{qr.initial_quantity}</TableCell>
                      <TableCell>
                        <span className={qr.remaining_quantity === 0 ? 'text-red-600 font-bold' : qr.remaining_quantity < qr.initial_quantity * 0.2 ? 'text-orange-600 font-bold' : 'text-green-600 font-bold'}>
                          {qr.remaining_quantity}
                        </span>
                      </TableCell>
                      <TableCell>
                        {qr.status === 'active' ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle size={12} className="mr-1" /> Active
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800">
                            <AlertTriangle size={12} className="mr-1" /> Depleted
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{new Date(qr.production_date).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewCartons(qr)}
                          title="View Individual Cartons"
                          data-testid={`view-cartons-btn-${qr.id}`}
                        >
                          <Boxes size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewQRCode(qr)}
                          title="View Details"
                        >
                          <Eye size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadQRCode(qr)}
                          title="Download QR Code"
                        >
                          <Download size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* View QR Code Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>QR Code Details</DialogTitle>
          </DialogHeader>
          {selectedQR && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="border-4 border-gray-200 rounded-lg p-4 bg-white">
                  <img 
                    src={`${API}/product-qrcodes/${selectedQR.id}/image`} 
                    alt="QR Code" 
                    className="w-48 h-48"
                  />
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-lg font-bold">{selectedQR.product_name}</p>
                <p className="text-sm text-gray-600">Batch: {selectedQR.batch_number}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Barcode:</span>
                  <code className="bg-white px-2 py-1 rounded">{selectedQR.barcode}</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Initial Quantity:</span>
                  <span className="font-semibold">{selectedQR.initial_quantity} cartons</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Remaining:</span>
                  <span className={`font-bold ${selectedQR.remaining_quantity === 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {selectedQR.remaining_quantity} cartons
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sold:</span>
                  <span className="font-semibold text-orange-600">
                    {selectedQR.initial_quantity - selectedQR.remaining_quantity} cartons
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Unit Price:</span>
                  <span className="font-semibold">Tsh {selectedQR.unit_price?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Production Date:</span>
                  <span>{new Date(selectedQR.production_date).toLocaleDateString()}</span>
                </div>
                {selectedQR.expiry_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Expiry Date:</span>
                    <span>{new Date(selectedQR.expiry_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button 
                  className="flex-1" 
                  onClick={() => downloadQRCode(selectedQR)}
                >
                  <Download size={16} className="mr-2" />
                  Download QR Code
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/product/scan/${selectedQR.id}`);
                    toast.success('Link copied to clipboard');
                  }}
                >
                  Copy Link
                </Button>
              </div>
              
              {selectedQR.scan_history && selectedQR.scan_history.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold mb-2">Scan History</h4>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {selectedQR.scan_history.map((scan, idx) => (
                      <div key={idx} className="text-sm bg-gray-50 p-2 rounded flex justify-between">
                        <span>{new Date(scan.scanned_at).toLocaleString()}</span>
                        <span className="text-orange-600">-{scan.quantity_sold} sold</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Individual Cartons Dialog */}
      <Dialog open={cartonsOpen} onOpenChange={setCartonsOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="cartons-dialog">
          <DialogHeader>
            <DialogTitle>
              Individual Carton QR Codes
              {cartonsBatch && (
                <span className="ml-2 text-sm font-normal text-gray-600">
                  - Batch: {cartonsBatch.batch_number} ({cartons.length} cartons)
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-700">
                Each carton has a unique QR code with format <code className="bg-white px-2 py-0.5 rounded">YYYYMMDD-Cnnnn</code>.
                Print these labels to attach to physical boxes for unique tracking.
              </p>
              <Button
                onClick={printAllCartons}
                disabled={!cartons.length}
                className="bg-[#EA5829] hover:bg-[#d24a20] text-white"
                data-testid="print-all-cartons-btn"
              >
                <Printer size={16} className="mr-2" />
                Print All Labels
              </Button>
            </div>

            {cartonsLoading ? (
              <div className="text-center py-12 text-gray-500">Loading cartons...</div>
            ) : cartons.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No cartons found for this batch.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {cartons.map((c) => (
                  <div
                    key={c.id}
                    className="border rounded-lg p-3 text-center bg-white hover:shadow-md transition-shadow"
                    data-testid={`carton-card-${c.id}`}
                  >
                    <img
                      src={`${API}/cartons/${c.id}/qr-image`}
                      alt={`QR ${c.id}`}
                      className="w-full aspect-square object-contain border rounded"
                    />
                    <div className="mt-2 font-mono text-xs font-semibold text-gray-900">{c.id}</div>
                    <div className="text-xs text-gray-600 truncate" title={c.product_name}>
                      {c.product_name}
                    </div>
                    <Badge
                      className={
                        c.status === 'active'
                          ? 'bg-green-100 text-green-800 mt-1'
                          : c.status === 'sold-via-scan'
                          ? 'bg-orange-100 text-orange-800 mt-1'
                          : c.status === 'delivered'
                          ? 'bg-blue-100 text-blue-800 mt-1'
                          : c.status === 'scanned'
                          ? 'bg-purple-100 text-purple-800 mt-1'
                          : 'bg-gray-100 text-gray-800 mt-1'
                      }
                    >
                      {c.status}
                    </Badge>
                    {c.last_scanned_by?.customer_name && (
                      <div className="text-[10px] text-gray-700 mt-1 truncate" title={c.last_scanned_by.customer_name}>
                        Scanned by: <b>{c.last_scanned_by.customer_name}</b>
                      </div>
                    )}
                    {c.last_scanned_by?.location && (
                      <div className="text-[10px] text-gray-500 truncate flex items-center justify-center gap-1">
                        <MapPin size={9} />
                        {c.last_scanned_by.location}
                      </div>
                    )}
                    {c.scan_count > 0 && (
                      <div className="text-[10px] text-gray-500 mt-1">Scans: {c.scan_count}</div>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-1 w-full"
                      onClick={() => downloadCartonQR(c)}
                      data-testid={`download-carton-${c.id}`}
                    >
                      <Download size={12} className="mr-1" /> Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
